import { Controller } from "@hotwired/stimulus"
import Rails from "@rails/ujs"
import { forceSetValueOnSelect, isEmpty, valueChangedFromDefault, isoDateToString, showNotification, addMonthsRailsStyle, isTurboNativeApp, setValueAndDispatch } from "./helpers"

export default class extends Controller {
  static targets = ["body", "send", "buyerAdditionalGroup", "buyerAdditionalOpen", "buyerAdditionalClose",
    "taxNoElem", "forTaxNo", "invoiceAdditionalGroup", "invoiceAdditionalClose", "invoiceAdditionalOpen", "currency", "allCurrencies",
    "discount", "sellDate", "issueDate", "issuePlace", "deliveryDate", "warehousesSelect", "departmentSelect",
    "buyerPostalAddressWrapper", "showAttachments", "invoiceTemplateSelect", "descriptionField", "attachmentsList",
    "exchangeKind", "exchangeCurrency", "exchangeKindWrapper", "ownExchangeDetails", "cancelModal", "additionalInfo",
    "descriptionLongField", "prepaymentExactAmountField", "positionsContainer", "paymentReferenceNumber",
    "massPaymentPattern", "massPaymentCode", "sellerBankAccount", "showIban", "buyerCountry",
    "deliveryAddressWrapper", "condNote", "sellerCountry", "usePrimeCee", "primeCeeWrapper", "form",
    "documentInfoWrapper", "paymentModal", "viesButton", "bdoNo", "priceList", "roundPrice",
    "sellerBankAccountInfo", "sellerBank", "sellerBankSwift", "buyerEmail", "discountElem", "sellerPostalAddressWrapper",
    "kind", "ksefOfflineWarningModal", "govSend", "cashAccounting",
    "emptyUnitNotice", "govSendBtn", "govSendSaveBtn", "govSavePrimaryBtn"
  ]

  defaultSellDate
  defaultCondNote
  defaultPriceList

  static values = {
    locale: String,
    newRecord: Boolean,
    correction: Boolean,
    govCorrection: Boolean,
    kind: String,
    // stawka wymuszona rodzajem dokumentu (dziś: ryczałt RR); pusty string = zwykła logika stawki.
    // Jedyne źródło prawdy to Invoice#forced_position_tax przekazywane data-atrybutem
    forcedPositionTax: String,
    income: Boolean,
    translations: Object,
    departments: Object,
    accountTemplateId: Number,
    accountInvoiceDescription: String,
    myWarehousesForSelect: Array,
    myWarehousesForSelectByDepartment: Object,
    warehouseId: Number,
    defaultLang: String,
    exchangeRatesAvailableForBanks: Object,
    accountCurrency: String,
    accountInvoiceDescriptionLong: String,
    kindDescription: String,
    netVisible: Boolean,
    grossVisible: Boolean,
    discountKind: String,
    showAdditionalInfo: Boolean,
    spareColClass: String,
    defaultNumber: String,
    defaultPattern: String,
    defaultKey: String,
    accountUseMassPayment: Boolean,
    accountCountry: String,
    euCountries: Array,
    dromCountries: Array,
    comCountries: Array,
    defaultCondNotes: Object,
    handleCondNote: Boolean,
    defaultExpiryDate: String,
    viesUrl: String,
    ksefEnabled: Boolean,
    accountUseMoss: Boolean,
    priceSetup: Object,
    usePriceList: Boolean,
    defaultCurrency: String,
    fromInvoiceSellerBankAccount: String,
    fromInvoiceSellerBank: String,
    sellerBankAccountConnectedWithDepartment: Boolean,
    departmentBankAccount: String,
    fromInvoiceSellerBankSwift: String,
    showGtuAlerts: Boolean,
    defaultProductUnit: String,
    govPermissions: Object,
  }

  static outlets = ["invoice--position-calculator", "invoice--lang", "currency", "moss", "invoice--contractor", "dropdown-invoice-bank-account", "invoice--inline-estimate", "invoice--tax-rate", "invoice--reverse-charge"]

  connect() {
    if (this.hasSendTarget && this.sendTarget.classList.contains("errors")) {
      this.showForm()
      this.sendTarget.classList.remove("errors")
    }

    if (this.hasSellDateTarget) {
      this.defaultSellDate = this.sellDateTarget.value
    }

    if (this.hasPriceListTarget) {
      this.defaultPriceList = this.priceListTarget.value
    }

    if (this.hasMassPaymentCodeTarget) {
      document.getElementById("invoice_buyer_mass_payment_code")?.dispatchEvent(new Event("change"))
    }

    this.updateWarehouseChoices()

    if (this.hasFormTarget) {
      this.isSubmitting = false
      this.boundHandleSubmitEnd = this.handleSubmitEnd.bind(this)
      this.boundHandleSubmit = this.handleSubmit.bind(this)
      this.formTarget.addEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
      this.formTarget.addEventListener("submit", this.boundHandleSubmit)
    }

    this.defaultCondNote = this.newRecordValue

    if (this.localeValue === "fr" && this.newRecordValue) {
      this.changeSellDate()
    }

    if (this.hasViesButtonTarget) {
      this.updateViesVisibility()
    }

    this.showGtuCodesOnInvoiceOnlyAlert()
    this.setBankAccountNumberFromInvoice()
    const paymentToKindElem = this.element.querySelector('#invoice_payment_to_kind')
    if (this.accountCountryValue === "pl" && (this.newRecordValue || (!this.newRecordValue && paymentToKindElem.value === "off")) && !["kp", "kw"].includes(this.kindValue)) {
      this.paymentToKindToPaymentTo(paymentToKindElem.value, this.element.querySelector('#invoice_payment_to'))
    }

    // Nasłuchuj zmian quantity_unit i cen, żeby pokazywać/ukrywać notice o braku jednostki
    if (this.hasEmptyUnitNoticeTarget) {
      this.element.addEventListener("change", (e) => {
        if (e.target.id?.match(/_(quantity_unit|price_net|price_gross|total_price_gross)$/)) {
          this.checkEmptyUnitNotice()
        }
      })
      this.checkEmptyUnitNotice()
    }

    this.element.addEventListener("invoice--tax:changed", this.updateTax.bind(this))
  }

  disconnect() {
    // Sprzatamy tylko pare submit/submit-end: zduplikowany listener submit psuje stan
    // isSubmitting (guard dwoi sie i ubija pierwszy submit); pozostale listenery
    // z connect() sa idempotentne, wiec duplikat po reconnect jest nieszkodliwy.
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("turbo:submit-end", this.boundHandleSubmitEnd)
      this.formTarget.removeEventListener("submit", this.boundHandleSubmit)
    }
  }

  handleSubmit(event) {
    // Tylko jeden submit naraz; kolejne dlawimy do turbo:submit-end,
    // bo Turbo wylacza klikniety przycisk dopiero po starcie requestu.
    if (this.isSubmitting || !this.checkBankAccountCurrency()) {
      event.preventDefault()
      event.stopImmediatePropagation()
      return
    }
    this.isSubmitting = true
  }

  async handleSubmitEnd() {
    this.isSubmitting = false
    await this.waitForTurboLoad()
    this.handleNewPageLoaded()
  }

  async warnKsefOffline(event) {
    const { kinds, today, previous_business_day: previousBusinessDay, issue_date_was: issueDateWas } = event.params.ksefOfflineWarning
    const value = this.issueDateTarget.value
    const restoreDate = issueDateWas || today
    this.lastApprovedDate ||= restoreDate
    const kindValue = this.hasKindTarget ? this.kindTarget.value : document.getElementById("invoice_kind")?.value
    if (kinds.includes(kindValue) && value < today && value !== this.lastApprovedDate) {
      event.preventDefault()
      event.stopImmediatePropagation()

      const severity = value >= previousBusinessDay ? "mild" : "severe"
      const modal = this.ksefOfflineWarningModalTarget
      modal.querySelector('[data-ksef-severity="mild"]').classList.toggle("hidden", severity !== "mild")
      modal.querySelector('[data-ksef-severity="severe"]').classList.toggle("hidden", severity !== "severe")

      modal.addEventListener("modal:value", (event) => {
        if (event.value === "true") {
          // Ok — akceptuj wybraną datę, zamknij kalendarz
          this.lastApprovedDate = value
          // setTimeout bo modal przywraca focus na input po modal:value, co ponownie otwiera flatpickr
          setTimeout(() => this.issueDateTarget._flatpickr?.close(), 0)
        } else {
          // Anuluj — przywróć datę z bazy (edycja) lub dzisiejszą (nowa faktura)
          this.issueDateTarget._flatpickr?.setDate(restoreDate, true)
          this.issueDateTarget.value = restoreDate
        }
        this.issueDateTarget.dispatchEvent(new Event('change', { bubbles: true })) // istotne zeby odpalic tez inne event handlery po warnKsefOffline, np. aktualizacja sellDate
      }, { once: true })

      this.issueDateTarget.blur()
      modal.dispatchEvent(new Event("modal:open"))
    } else {
      this.lastApprovedDate = value
    }
  }

  waitForTurboLoad() {
    return new Promise((resolve) => {
      const handleLoad = () => {
        resolve()
        document.removeEventListener("turbo:load", handleLoad)
      }
      document.addEventListener("turbo:load", handleLoad)
    })
  }

  handleNewPageLoaded() {
    if (this.newRecordValue && isTurboNativeApp()) {
      const invElem = document.querySelector("[data-controller='invoice']")
      if (invElem) {
        window.TurboNativeBridge.postMessage("sendEvent", {
          event: "create_invoice",
          incomes: invElem.dataset.incomes,
          expenses: invElem.dataset.expenses
        })
      }
    }
  }

  updateWarehouseChoices() {
    if (this.hasWarehousesSelectWithOptions()) {
      const departmentId = this.hasDepartmentSelectTarget ? this.departmentSelectTarget.value : ""
      const departmentWarehouses = this.warehousesForDepartment(departmentId)
      const newWarehousesSelectOptions = this.warehousesSelectOptions(departmentWarehouses)

      this.warehousesSelectTarget.replaceChildren(...newWarehousesSelectOptions)
      if (departmentWarehouses.find(w => w[1] === this.warehouseIdValue) === undefined) {
        this.warehousesSelectTarget.value = this.warehouseIdValue = this.warehousesSelectTarget.firstChild.value
        showNotification(this.translationsValue["warehouse_has_changed"], true, ["{warehouse_name}"], [this.warehousesSelectTarget.firstChild.innerText])
      } else {
        this.warehousesSelectTarget.value = this.warehouseIdValue
      }
    }
  }

  warehousesSelectChanged() {
    this.warehouseIdValue = this.warehousesSelectTarget.value
  }

  warehousesSelectOptions(warehousesForSelect) {
    return warehousesForSelect.map(([name, value]) => {
      const option = document.createElement("option")
      option.value = value
      option.innerText = name
      return option
    })
  }

  warehousesForDepartment(departmentId) {
    if (departmentId === "" || (this.myWarehousesForSelectByDepartmentValue[departmentId] ?? []).length < 1) {
      return this.myWarehousesForSelectValue
    } else {
      return this.myWarehousesForSelectByDepartmentValue[departmentId]
    }
  }

  hasWarehousesSelectWithOptions() {
    return this.hasWarehousesSelectTarget && this.warehousesSelectTarget.firstChild != null
  }

  showForm() {
    if (this.sendTarget.classList.contains('hidden')) {
      this.bodyTarget.classList.add('hidden')
      this.sendTarget.classList.remove('hidden')
    }
  }

  emailChange() {
    document.getElementById("email_change_div").classList.remove("hidden")
    document.querySelectorAll('input[name="invoice[email_change]"]').forEach(input => {
      input.disabled = false
    })
  }

  changeReplyTo() {
    const selectBox = document.getElementById("invoice_email_from")
    const value = selectBox.options[selectBox.selectedIndex].text
    document.getElementById("invoice_reply_to").value = value
  }

  hide() {
    if (this.bodyTarget.classList.contains('hidden')) {
      this.sendTarget.classList.add('hidden')
      this.bodyTarget.classList.remove('hidden')
      this.bodyTarget.scrollIntoView()
    }
  }

  switchBuyerType(event) {
    if (event.target.value === "true") {
      document.querySelectorAll("[data-buyer-person]").forEach((element) => element.classList.add("hidden"))
      document.querySelectorAll("[data-buyer-company]").forEach((element) => element.classList.remove("hidden"))
      this.forTaxNoTarget.before(this.taxNoElemTarget)
      if (this.localeValue === "fr") {
        this.taxNoElemTarget.classList.remove("hidden")
      }
    } else {
      document.querySelectorAll("[data-buyer-company]").forEach((element) => element.classList.add("hidden"))
      document.querySelectorAll("[data-buyer-person]").forEach((element) => element.classList.remove("hidden"))
      this.buyerAdditionalGroupTarget.append(this.taxNoElemTarget)
      if (this.localeValue === "fr") {
        this.taxNoElemTarget.classList.add("hidden")
      } else if (this.localeValue === "pl" && this.newRecordValue) {
        const taxKindElem = this.element.querySelector("[data-invoice--tax-target=\"kind\"]")
        if (taxKindElem && taxKindElem.querySelector("option[value='empty']")) {
          setValueAndDispatch(taxKindElem, "empty")
        }
      }
    }
  }

  toggleBuyerAdditional() {
    [this.buyerAdditionalGroupTarget, this.buyerAdditionalCloseTarget, this.buyerAdditionalOpenTarget].forEach((element) => element.classList.toggle("hidden"))
  }

  toggleInvoiceAdditional() {
    [this.invoiceAdditionalGroupTarget, this.invoiceAdditionalCloseTarget, this.invoiceAdditionalOpenTarget].forEach((element) => element.classList.toggle("hidden"))
  }

  showDepartmentForm() {
    this.element.querySelector("#invoice_checkbox_show_seller").value = "1"
    this.element.querySelectorAll("[data-no-seller]").forEach((element) => element.classList.add("hidden"))
    this.element.querySelectorAll("[data-seller]").forEach((element) => element.classList.remove("hidden"))
  }

  changeDocumentNumber(event) {
    const keepIfSamePattern = event.params.keepPattern || false
    const testMode = false // test_mode = $("#" + prefix + "_test").is(":checked")
    const number = document.getElementById('invoice_number').value
    const departmentId = document.getElementById('invoice_department_id')?.value
    if (this.defaultNumberValue !== '' && this.defaultKeyValue === (this.issueDateTarget.value + "-" + departmentId + "-" + this.kindValue + "-" + testMode)) {
      this.updateInvoiceNumber(this.defaultPatternValue, this.defaultNumberValue)
    } else {
      const params = {
        issue_date: this.issueDateTarget.value,
        kind: this.kindValue,
        document_id: event.params.id || '',
        document_type: 'invoice',
        test_mode: testMode
      }

      if (departmentId) {
        params.department_id = departmentId
      }

      Rails.ajax({
        type: 'get',
        url: '/invoices/get_new_number',
        dataType: 'json',
        data: new URLSearchParams(params),
        success: (data) => {
          if (data['nr'] !== document.getElementById("number_check").value && !(data['pattern'] === document.getElementById("invoice_pattern").value && keepIfSamePattern)) {
            if (number !== '' && number !== document.getElementById("number_check").value) {
              showNotification(this.translationsValue["pattern_message_not_change"])
            } else {
              this.updateInvoiceNumber(data['pattern'], data['nr'])
              showNotification(this.translationsValue["pattern_message_change"], true, ["{number_from}", "{number_to}"], [number, data["nr"]])
            }
          }
        }
      })
    }
  }

  updateInvoiceNumber(pattern, number) {
    document.getElementById("invoice_pattern").value = pattern
    document.getElementById("number_check").value = number
    document.getElementById("invoice_number").value = number
  }

  handlePaymentToChange(event) {
    const dateField = this.element.querySelector('#invoice_payment_to')
    if (event.target.value === "other_date") {
      dateField.classList.remove("hidden")
    } else if (this.accountCountryValue === "pl") {
      this.paymentToKindToPaymentTo(event.target.value, dateField)
    } else {
      dateField.classList.add("hidden")
    }
  }

  paymentToKindToPaymentTo(paymentToKindVal, dateField) {
    const svgIcon = dateField.closest('.relative')?.querySelector('svg')
    let parsedPaymentToKindVal
    dateField.classList.remove("hidden")
    svgIcon?.classList.remove("hidden")
    switch (paymentToKindVal) {
      case 'off':
        dateField.classList.add("hidden")
        svgIcon?.classList.add("hidden")
        parsedPaymentToKindVal = '1m'
        break
      case '1m':
        parsedPaymentToKindVal = '1m'
        break
      case 'other_date':
      case 'other':
        break
      default:
        parsedPaymentToKindVal = paymentToKindVal.match(/[-]?\d+/g)?.at(0)
        if (parsedPaymentToKindVal === undefined) { // ktoś może mieć w słowniku z ustawień konta sam tekst
          parsedPaymentToKindVal = '1m'
        }
    }
    if (parsedPaymentToKindVal !== undefined) {
      const issueDate = new Date(document.querySelector('#invoice_issue_date').value)
      let paymentToNewDate
      if (parsedPaymentToKindVal === '1m') {
        paymentToNewDate = addMonthsRailsStyle(issueDate, 1)
      } else {
        paymentToNewDate = new Date(issueDate.setDate(issueDate.getDate() + Number(parsedPaymentToKindVal)))
      }
      dateField.value = isoDateToString(paymentToNewDate)
    }
  }

  paymentToToPaymentToKind() {
    if (this.accountCountryValue === "pl") {
      const dateFieldSelect = this.element.querySelector('select[name="invoice[payment_to_kind]"]')
      const dateFieldInput = this.element.querySelector('input[name="invoice[payment_to_kind]"]')
      if (dateFieldSelect !== null) {
        dateFieldSelect.value = "other_date"
      }
      if (dateFieldInput !== null) {
        dateFieldInput.value = ""
      }
    }
  }

  changePartialStatus(event) {
    const status = event.target.value
    if (status === "partial" || status === "paid") {
      document.querySelector("[data-partial-paid]").classList.remove("hidden")
    } else {
      document.querySelector("[data-partial-paid]").classList.add("hidden")
    }

    // Gdy status zmienia się na "paid", wpisz price_to_pay do pola paid
    if (status === "paid") {
      const paidInput = document.getElementById("invoice_paid")
      if (paidInput) {
        paidInput.value = this.calculatePriceToPayForForm()
      }
    }
  }

  calculatePriceToPayForForm() {
    const parseAmount = (text) => {
      // Usuń spacje i nie-łamiące spacje (separatory tysięcy), zamień przecinek na kropkę
      return parseFloat(text.replace(/[\s\u00a0]/g, "").replace(",", ".")) || 0
    }

    // Suma brutto z formularza (po rabacie jeśli jest)
    const discountedEl = document.querySelector("[data-invoice--position-target~='summaryGrossDiscounted']")
    const grossEl = document.querySelector("[data-invoice--position-target~='summaryGross']")
    const activeGrossEl = (discountedEl && !discountedEl.classList.contains("hidden")) ? discountedEl : grossEl
    const gross = activeGrossEl ? parseAmount(activeGrossEl.textContent) : 0

    // Sumy settlement (obciążenia i odliczenia)
    let chargesSum = 0
    let deductionsSum = 0
    const chargesEl = document.querySelector("[data-invoice--settlement-position-target='chargesSum']")
    const deductionsEl = document.querySelector("[data-invoice--settlement-position-target='deductionsSum']")
    if (chargesEl) {
      const wrap = chargesEl.closest("[data-settlement-sum]")
      if (!wrap || !wrap.classList.contains("hidden")) {
        chargesSum = parseAmount(chargesEl.textContent)
      }
    }
    if (deductionsEl) {
      const wrap = deductionsEl.closest("[data-settlement-sum]")
      if (!wrap || !wrap.classList.contains("hidden")) {
        deductionsSum = parseAmount(deductionsEl.textContent)
      }
    }

    const priceToPayVal = Math.round((gross + chargesSum - deductionsSum) * 100) / 100
    // Formatowanie zależne od locale (PL: przecinek, reszta: kropka)
    const formatted = priceToPayVal.toFixed(2)
    return document.documentElement.lang === "pl" ? formatted.replace(".", ",") : formatted
  }

  // deleguje do invoice--tax-rate (właściciela logiki stawek VAT) — patrz tax_rate_controller.js
  updateNaKindVisibility() {
    this.invoiceTaxRateOutlet.updateNaKindVisibility()
  }

  updateNaKind(event) {
    this.invoiceTaxRateOutlet.updateNaKind(event)
  }

  updateExemptTaxVisibility() {
    this.invoiceTaxRateOutlet.updateExemptTaxVisibility()
  }

  checkPositions(event) {
    let confBox = false
    document.querySelectorAll("[id^=invoice_positions_attributes_][id$='_name']").forEach((element, index) => {
      if (document.getElementById("invoice_positions_attributes_" + index + "_save_to_database") &&
        !document.getElementById("invoice_positions_attributes_" + index + "_save_to_database").checked &&
        !document.getElementById("invoice_positions_attributes_" + index + "__destroy").checked) {
        if (document.getElementById("invoice_positions_attributes_" + index + "_product_id").value.length === 0 &&
          document.getElementById("invoice_positions_attributes_" + index + "_name").value.length > 0) {
          confBox = true
        }
      }
    })

    if (event.params.productStrategy !== "create_if_new") {
      confBox = false
    }

    if (confBox && !event.params.forceNotSavingProduct) {
      const confirmed = confirm(event.params.productAddConfirm)
      if (!confirmed) {
        event.preventDefault()
        return false
      }
    }
  }

  changeDiscount() {
    this.discountTarget.checked = false
    const selector = this.discountKindValue === "amount"
      ? "[id^='invoice'][id*='positions_attributes_'][id$='_discount']"
      : "[id^='invoice'][id*='positions_attributes_'][id$='_discount_percent']"

    const filled = Array.from(document.querySelectorAll(selector)).filter((elem) => {
      return elem.value.length > 0 && parseFloat(elem.value) > 0
    })

    if (filled.length > 0) {
      this.discountTarget.checked = true
    }

    this.discountTarget.dispatchEvent(new Event("change"))
  }

  changeAdditionalInfo() {
    this.additionalInfoTarget.checked = false
    const additionalInfo = Array.from(document.querySelectorAll("[id^=invoice_positions_attributes_][id$='_additional_info']")).filter((elem) => {
      return (elem.value.length > 0)
    })

    if (additionalInfo.length > 0) {
      this.additionalInfoTarget.checked = true
    }
  }

  setGlobalDiscount(event) {
    document.querySelectorAll("[id^='invoice'][id*='positions_attributes_'][id$='_discount_percent']").forEach((elem) => {
      elem.value = event.target.value
      elem.dispatchEvent(new Event("change"))
    })
  }

  changeDiscountKind(event) {
    this.discountKindValue = event.target.value
    if (event.target.value === 'amount') {
      document.querySelectorAll("[data-discount-amount]").forEach((element) => element.classList.remove('hidden'))
      document.querySelectorAll("[data-discount-percent]").forEach((element) => element.classList.add('hidden'))
    } else if (event.target.value !== 'amount') {
      document.querySelectorAll("[data-discount-amount]").forEach((element) => element.classList.add('hidden'))
      document.querySelectorAll("[data-discount-percent]").forEach((element) => element.classList.remove('hidden'))
    }
  }

  changeSellDate() {
    if (this.localeValue === "pl") {
      // Auto-fill sell_date z issue_date tylko przy tworzeniu nowej faktury.
      // Przy edycji istniejącej faktury sell_date jest historyczny i nie powinien być nadpisywany.
      if (this.newRecordValue && this.defaultSellDate === this.sellDateTarget.value && this.issueDateTarget.value !== this.sellDateTarget.value) {
        this.defaultSellDate = this.sellDateTarget.value = this.issueDateTarget.value
      }
    }
    if (this.localeValue === "fr" && this.defaultExpiryDateValue !== "") {
      if (this.hasSellDateTarget) {
        const prevDate = new Date(this.sellDateTarget.value)
        if (this.defaultSellDate === this.sellDateTarget.value && document.getElementById("invoice_sell_date_kind")?.value !== "") {
          const issueDate = new Date(this.issueDateTarget.value)
          switch (this.defaultExpiryDateValue) {
            case "one_month":
              issueDate.setMonth(issueDate.getMonth() + 1)
              break
            case "two_months":
              issueDate.setMonth(issueDate.getMonth() + 2)
              break
            case "three_months":
              issueDate.setMonth(issueDate.getMonth() + 3)
              break
            case "fifteen_days":
              issueDate.setDate(issueDate.getDate() + 15)
              break
            case "seven_days":
              issueDate.setDate(issueDate.getDate() + 7)
              break
          }
          this.defaultSellDate = this.sellDateTarget.value = isoDateToString(issueDate)
        } else {
          this.defaultSellDate = this.sellDateTarget.value = this.issueDateTarget.value
        }
        if (!this.newRecordValue && isoDateToString(prevDate) !== this.sellDateTarget.value) {
          showNotification(this.translationsValue["date_auto_change_message"], true, ["{sell_date}"], [this.defaultSellDate])
        }
      }
    }
  }

  // tu się dzieje analog funkcji set_dept na desktopie
  setFromDepartment() {
    const deptId = this.departmentSelectTarget.value
    const deptAttributes = this.departmentsValue[deptId] ?? {}
    // Firmlet.force_set_val_on_select("#invoice_seller_country", depts[id]["country"])
    // hurtowe wypełnienie wszystkich pasujących pól w sellerze (na mobile nie ma ich dużo)
    for (const key in deptAttributes) {
      const field = this.element.querySelector(`#invoice_seller_${key}`)
      if (field !== null) {
        field.value = deptAttributes[key]
      }
    }

    document.getElementById("invoice_seller_tax_no_kind")?.dispatchEvent(new Event("change", { bubbles: true }))
    document.getElementById("invoice_seller_tax_no")?.dispatchEvent(new CustomEvent("change", { bubbles: true, detail: { source: "autocomplete" } }))

    if (this.hasBdoNoTarget) {
      if (isEmpty(deptAttributes["bdo_no"])) {
        this.bdoNoTarget.classList.add("hidden")
      } else {
        this.bdoNoTarget.classList.remove("hidden")
      }
    }

    if (this.hasCashAccountingTarget) {
      const ca = deptAttributes["cash_accounting"] === true

      if (this.kindValue !== "vat_mp") {
        this.cashAccountingTarget.checked = ca
      }
    }

    // wypełnienie wybranych pól na invoicie polami z działu
    // niektóre nie występują na mobile ale lepiej nie zgubić
    const nonSellerOverrideFields = [
      "bank_iban",
      "seller_sort_code_uk",
      "seller_bank_account_name_uk",
      "seller_trade_register_cz",
    ]
    for (const key of nonSellerOverrideFields) {
      if (key in deptAttributes) {
        const field = this.element.querySelector(`#invoice_${key}`)
        if (field !== null) {
          field.value = deptAttributes[key]
        }
      }
    }

    if (this.hasDropdownInvoiceBankAccountOutlet) {
      this.dropdownInvoiceBankAccountOutlet.departmentIdValue = this.departmentSelectTarget.value
    }

    if (this.hasDescriptionLongFieldTarget && "invoice_description_long" in deptAttributes) {
      this.descriptionLongFieldTarget.value = deptAttributes["invoice_description_long"] ?? this.accountInvoiceDescriptionLongValue
    }

    if ("issuers" in deptAttributes && Object.keys(deptAttributes["issuers"]).length > 0) {
      this.invoiceContractorOutlets.forEach(contractor => {
        if (contractor.kindValue === "issuer") {
          contractor.element.classList.add("hidden")
          contractor.destroyTarget.checked = true
        }
      })

      Object.keys(deptAttributes["issuers"]).forEach(key => {
        document.querySelector("[data-target='buttonIssuer'] > button")?.click()
        requestAnimationFrame(() => {
          const last = this.invoiceContractorOutlets
            .filter(o => o.kindValue === "issuer")
            .at(-1)
          if (last) {
            last.fillContractor(deptAttributes["issuers"][key])
          }
        })
      })
    }

    // pominięte segmenty (bo robiłyby się regułą na pola sellera):
    // - bank_swift

    // zmiana uwag zależnie od działu
    if (this.hasDescriptionFieldTarget) {
      if (!isEmpty(deptAttributes["invoice_description"]) || (isEmpty(this.accountInvoiceDescriptionValue) && !valueChangedFromDefault(this.descriptionFieldTarget))) {
        this.descriptionFieldTarget.value = deptAttributes["invoice_description"]
        if (!isEmpty(this.kindDescriptionValue)) {
          this.descriptionFieldTarget.value += (isEmpty(deptAttributes["invoice_description"]) ? "" : "\n") + this.kindDescriptionValue
        }
        this.descriptionFieldTarget.defaultValue = this.descriptionFieldTarget.value
      }
    } else {
      const deptDescription = deptAttributes["invoice_description_own"] || this.accountInvoiceDescriptionValue || ""
      window.dispatchEvent(new CustomEvent("department-changed", { detail: { content: deptDescription } }))
    }

    // pominięte: wykonanie change_document_number (ma się dobrze tam gdzie jest)
    // przepisanie waluty z konta działu
    const bankAccountCurrency = deptAttributes["bank_account_currency"]
    if (!isEmpty(bankAccountCurrency) && this.currencyOutlet.currencyTarget.value !== bankAccountCurrency) {
      forceSetValueOnSelect(this.currencyOutlet.currencyTarget, bankAccountCurrency)
      this.currencyOutlet.currencyTarget.dispatchEvent(new CustomEvent("change"))
      showNotification(this.translationsValue["currency_has_changed"], true, ["{currency}"], [bankAccountCurrency])
    }
    // zmiana domyślnego podatku w pustych pozycjach
    this.invoiceTaxRateOutlet.applyDepartmentDefaultTaxToPositions(deptAttributes)
    this.invoiceTaxRateOutlet.forceFrDepartmentTaxIfReverseChargeOff(deptAttributes)
    // zmiana szablonu zależnie od działu
    const invoiceTemplateId = (deptAttributes["invoice_template_id"] ?? this.accountTemplateIdValue).toString()
    if (this.hasInvoiceTemplateSelectTarget && this.invoiceTemplateSelectTarget.value !== invoiceTemplateId) {
      this.invoiceTemplateSelectTarget.value = invoiceTemplateId
      showNotification(this.translationsValue["template_has_changed"], true, ["{template_name}"], [this.invoiceTemplateSelectTarget.selectedOptions[0].innerText])
    }

    //zmiana jezyka zaleznie od dzialu
    const invoiceLang = deptAttributes['invoice_lang'] || this.defaultLangValue
    const lang1 = invoiceLang.split('/')[0]
    const lang2 = invoiceLang.split('/')[1] || ''
    let changedLangs = false

    if (this.invoiceLangOutlet.lang1SelectTarget.value !== lang1) {
      this.invoiceLangOutlet.lang1SelectTarget.value = lang1
      changedLangs = true
    }

    if (this.invoiceLangOutlet.lang2SelectTarget.value !== lang2) {
      if (lang2 === '') {
        this.invoiceLangOutlet.removeSecondLang()
      } else {
        if (this.invoiceLangOutlet.lang2SelectTarget.value === '') { this.invoiceLangOutlet.addLangButtonTarget.click() }
        this.invoiceLangOutlet.lang2SelectTarget.value = lang2
        changedLangs = true
      }
    }

    if (changedLangs) {
      let langs = this.invoiceLangOutlet.lang1SelectTarget.selectedOptions[0].innerText
      if (this.invoiceLangOutlet.lang2SelectTarget.selectedOptions.length > 0) {
        langs += `/${this.invoiceLangOutlet.lang2SelectTarget.selectedOptions[0].innerText}`
      }
      showNotification(this.translationsValue["language_changed"], true, ["{language}"], [langs])
    }

    const useInvoiceIssuerElem = document.getElementById("invoice_use_invoice_issuer")
    if (useInvoiceIssuerElem) {
      useInvoiceIssuerElem.checked = deptAttributes["use_invoice_issuer"] === true
      useInvoiceIssuerElem.dispatchEvent(new Event("change"))
      document.getElementById("invoice_invoice_issuer").value = deptAttributes["correspondence_address"] || ""
    }

    if (this.hasSellerPostalAddressWrapperTarget) {
      const postalAddress = deptAttributes["postal_address"]
      const postalAddressElem = document.getElementById("seller_postal_address_checkbox")
      if (postalAddress) {
        Object.entries(deptAttributes["postal_address"]).forEach(([key, val]) => {
          this.sellerPostalAddressWrapperTarget.querySelector(`#invoice_seller_postal_address_attributes_${key}`).value = val
        })
      } else {
        this.sellerPostalAddressWrapperTarget.querySelector("button").click()
      }

      this.sellerPostalAddressWrapperTarget.querySelector(".delivery_address_old").value = deptAttributes["correspondence_address"] || ""
      this.sellerPostalAddressWrapperTarget.querySelectorAll("[data-old-address]").forEach(elem => {
        elem.classList.toggle("hidden", !!postalAddress || !deptAttributes["correspondence_address"])
      })

      postalAddressElem.checked = !!postalAddress
      postalAddressElem.dispatchEvent(new Event("change"))
    }
    // pominięte: lump_sum_tax
    // pominięte: cofnięcie zmiany działu - numer konta (nie mamy pól seller_bank_account seller_bank_iban)

    this.updateGovSendButton()
  }

  changeDeliveryDate(event) {
    if (this.hasDeliveryDateTarget && event.currentTarget.value !== this.deliveryDateTarget.value) {
      const deliveryDate = this.deliveryDateTarget.value
      this.deliveryDateTarget.value = event.currentTarget.value

      if (!(event.params.issueDateWas === event.params.deliveryDateWas && event.params.deliveryDateWas === deliveryDate)) {
        showNotification(this.translationsValue["delivery_date_changed"], true, ["{delivery_date}"], [event.currentTarget.value])
      }
    }
  }

  changeIssuePlace() {
    if (!this.hasIssuePlaceTarget) return

    const deptId = this.departmentSelectTarget.value
    const city = this.departmentsValue[deptId]?.city

    if (city && city !== this.issuePlaceTarget.value) {
      this.issuePlaceTarget.value = city
      this.issuePlaceTarget.dispatchEvent(new Event("change", { bubbles: true }))
      showNotification(this.translationsValue["invoice_issue_place_changed"])
    }
  }

  toggleAttachment() {
    this.attachmentsListTarget.classList.toggle("hidden")
  }

  downloadPDF(event) {
    if (isTurboNativeApp()) {
      window.TurboNativeBridge.postMessage("downloadPDF", { url: event.target.href, filename: event.params.pdfFilename })
    } else {
      window.open(event.target.href, "_blank")
    }
  }

  printPDF(event) {
    if (isTurboNativeApp()) {
      window.TurboNativeBridge.postMessage("printPDF", { url: event.params.pdfUrl, filename: event.params.pdfFilename })
    } else {
      window.open(event.params.pdfUrl, "_blank")
    }
  }

  handleExchangeCurrency() {
    const hasExchangeCurrency = this.exchangeCurrencyTarget.value !== ""
    if (hasExchangeCurrency) {
      this.exchangeKindWrapperTarget.classList.remove("hidden")
      this.notifyIfCurrencyNotInBank()
    } else {
      this.exchangeKindWrapperTarget.classList.add("hidden")
    }
    if (hasExchangeCurrency && this.exchangeKindTarget.value === "own") {
      this.ownExchangeDetailsTargets.forEach((element) => element.classList.remove("hidden"))
    } else {
      this.ownExchangeDetailsTargets.forEach((element) => element.classList.add("hidden"))
    }
  }

  notifyIfCurrencyNotInBank() {
    const currency = this.currencyTarget.value
    /** @type {HTMLSelectElement} */
    const kindSelect = this.exchangeKindTarget
    const kind = kindSelect.value.toUpperCase()
    const exchangeCurrency = this.exchangeCurrencyTarget.value
    if (kind !== "own".toUpperCase() && exchangeCurrency !== "" && currency !== this.accountCurrencyValue) {
      const availableCurrencies = this.exchangeRatesAvailableForBanksValue[kind]
      if (availableCurrencies != null && !availableCurrencies.includes(currency.toUpperCase())) {
        showNotification(this.translationsValue["currency_not_in_bank"], true, ["%{bank}"], [kindSelect.selectedOptions[0].innerText])
      }
    }
  }

  /** @param {Event & {target: HTMLSelectElement, params: {exactAmountValue: string}}} event */
  handlePrepaymentRequiredExactAmount(event) {
    if (event.target.value === event.params.exactAmountValue) {
      this.prepaymentExactAmountFieldTarget.classList.remove("hidden")
    } else {
      this.prepaymentExactAmountFieldTarget.classList.add("hidden")
    }
  }

  showCancelModal() {
    this.cancelModalTarget.classList.remove("hidden")
  }

  hideCancelModal() {
    this.cancelModalTarget.classList.add("hidden")
  }

  changePaymentReferenceNumber(event) {
    if (this.hasPaymentReferenceNumberTarget) {
      this.paymentReferenceNumberTarget.placeholder = event.currentTarget.value.replace(/[^0-9]/g, '')
    }
  }

  showWarning(event) {
    if (event.params.message) {
      showNotification(this.translationsValue[event.params.message])
    }
  }

  setMassPaymentBankAccount(event) {
    // New bank account has not an seller bank account field
    if (!this.hasSellerBankAccountTarget) {
      return
    }

    const code = event.currentTarget.value
    if (code !== '') {
      const bankAccount = code.indexOf(this.massPaymentPatternTarget.value) > -1 ? code : (this.massPaymentPatternTarget.value + code)
      this.sellerBankAccountTarget.value = bankAccount
    } else if (this.hasDepartmentSelectTarget) {
      const escapedAccount = this.departmentsValue[this.departmentSelectTarget.value]['bank_account'] ?? ''
      if (this.sellerBankAccountTarget.value !== escapedAccount) {
        if (confirm(this.translationsValue["change_bank_account_to_default"])) {
          this.sellerBankAccountTarget.value = escapedAccount
          if (this.hasSellerBankAccountInfoTarget) {
            this.sellerBankAccountInfoTarget.innerText = escapedAccount
          }
        }
      }
    }
  }

  checkIban() {
    if (this.hasShowIbanTarget) {
      if ((this.accountCountryValue === "cz" && this.currencyTarget.value !== "CZK" && this.buyerCountryTarget.value !== "CZ") ||
        (this.accountCountryValue === "sk" && this.currencyTarget.value === "EUR" && this.buyerCountryTarget.value !== "SK")) {
        this.showIbanTarget.checked = true
      } else {
        this.showIbanTarget.checked = false
      }
    }
  }

  updateCondNote() {
    if (this.defaultCondNote && this.buyerCountryTarget.value !== "") {
      const buyerCountry = this.buyerCountryTarget.value === "MC" ? "FR" : this.buyerCountryTarget.value
      const sellerCountry = this.sellerCountryTarget.value === "MC" ? "FR" : this.sellerCountryTarget.value

      if (sellerCountry === buyerCountry) {
        this.condNoteTarget.value = this.defaultCondNotesValue.national_cond_note
        return
      }

      if (this.dromCountriesValue.includes(buyerCountry)) {
        this.condNoteTarget.value = this.defaultCondNotesValue.drom_cond_note
        return
      }

      if (this.comCountriesValue.includes(buyerCountry)) {
        this.condNoteTarget.value = this.defaultCondNotesValue.com_cond_note
        return
      }

      if (this.hasMossOutlet && this.mossOutlet.useMossTarget.checked) {
        this.condNoteTarget.value = this.defaultCondNotesValue.oss_cond_note
        return
      }
      let noTax = true

      if (this.kindValue === "correction") {
        // TODO fv korekty
        // while ($('#invoices_positions_attributes_' + i + '_correction_after_attributes_tax').length > 0) {
        //  var curr_tax = $('#invoice_positions_attributes_' + i + '_correction_after_attributes_tax').val();
        //  if (curr_tax != 0 && curr_tax != 'disabled') {
        //   no_tax = false;
        //   break;
        //  }
        //  i++;
        // }
      } else {
        for (const elem of document.querySelectorAll("[id^=invoice_positions_attributes_][id$='_tax']")) {
          if (elem.value !== 0 && elem.value !== "disabled") {
            noTax = false
            break
          }
        }
      }

      if (noTax && this.euCountriesValue.includes(sellerCountry)) {
        if (this.euCountriesValue.includes(buyerCountry)) {
          this.condNoteTarget.value = this.defaultCondNotesValue.EU_cond_note
        } else {
          this.condNoteTarget.value = this.defaultCondNotesValue.outside_EU_cond_note
        }
        return
      }
      this.condNoteTarget.value = ""
    }
  }

  updateDefaultCondNote() {
    this.defaultCondNote = false
  }

  // deleguje do invoice--tax-rate (właściciela logiki stawek VAT) — patrz tax_rate_controller.js
  get autocompleteTax() {
    return this.invoiceTaxRateOutlet.autocompleteTax
  }

  get taxVisibleValue() {
    return this.invoiceTaxRateOutlet.taxVisibleValue
  }

  get defaultTax() {
    return this.invoiceTaxRateOutlet.defaultTax
  }

  fillWithDefaultTax(position = null) {
    this.invoiceTaxRateOutlet.fillWithDefaultTax(position)
  }

  setDefaultTax() {
    this.invoiceTaxRateOutlet.setDefaultTax()
  }

  toggleUsePrimeCee() {
    const checkbox = this.usePrimeCeeTarget
    checkbox.checked = !checkbox.checked
    if (checkbox.checked) {
      this.primeCeeWrapperTarget.classList.remove("hidden")
    } else {
      this.primeCeeWrapperTarget.classList.add("hidden")
    }
  }

  // deleguje do invoice--tax-rate (właściciela logiki stawek VAT) — patrz tax_rate_controller.js
  setTaxOptions(options) {
    return this.invoiceTaxRateOutlet.setTaxOptions(options)
  }

  setDefaultTaxes() {
    this.invoiceTaxRateOutlet.setDefaultTaxes()
  }

  closeDocumentInfo({ params: { noticeStatusUrl } }) {
    Rails.ajax({
      type: "post",
      url: noticeStatusUrl,
      success: () => this.documentInfoWrapperTarget.classList.add("hidden"),
    })
  }

  // Pokaż/ukryj notice o braku jednostki miary.
  // Widoczny gdy: wpisano cenę i istnieje pozycja bez jednostki (nie usunięta).
  // Na korekcie: notice powinien się pokazywać tylko gdy correction_after ma pustą jednostkę
  // (TODO: tailwind nie ma jeszcze formularza korekty z sekcjami before/after)
  checkEmptyUnitNotice() {
    if (!this.hasEmptyUnitNoticeTarget) return

    const priceFields = this.element.querySelectorAll('[id$="_price_net"], [id$="_price_gross"], [id$="_total_price_gross"]')
    let priceEntered = false
    priceFields.forEach(f => { if (f.value) priceEntered = true })
    if (!priceEntered) {
      this._hideEmptyUnitNotice()
      return
    }

    let hasEmptyUnit = false
    this.element.querySelectorAll('[id$="_quantity_unit"]:not([type="hidden"])').forEach(el => {
      const row = el.id.match(/\d+/)?.[0]
      // Pozycja usunięta - slideUp() (display:none) lub hidden class
      const detail = document.getElementById("detail_" + row + "_")
      if (detail && (detail.style.display === "none" || detail.classList.contains("hidden"))) return
      // Pozycja usunięta - checkbox _destroy
      const destroyCheck = document.getElementById("invoice_positions_attributes_" + row + "__destroy")
      if (destroyCheck && destroyCheck.checked) return
      if (!el.value) hasEmptyUnit = true
    })

    if (hasEmptyUnit) {
      this._showEmptyUnitNotice()
    } else {
      this._hideEmptyUnitNotice()
    }
  }

  _showEmptyUnitNotice() {
    const el = this.emptyUnitNoticeTarget
    if (!el.classList.contains("hidden")) return
    el.classList.remove("hidden")
    // requestAnimationFrame żeby transition zadziałał po usunięciu hidden
    requestAnimationFrame(() => {
      el.classList.remove("opacity-0")
      el.classList.add("opacity-100")
    })
  }

  _hideEmptyUnitNotice() {
    const el = this.emptyUnitNoticeTarget
    if (el.classList.contains("hidden")) return
    el.classList.remove("opacity-100")
    el.classList.add("opacity-0")
    el.addEventListener("transitionend", () => el.classList.add("hidden"), { once: true })
  }

  /**
   * @param {Event} event
   */
  confirmCreateFromEstimate(event) {
    const { params: { linkedAdvancesTranslation, lastAdvancePath } } = event
    const escaper = document.createElement("div")
    escaper.innerHTML = linkedAdvancesTranslation
    if (!confirm(escaper.innerText)) {
      event.preventDefault()
      Turbo.visit(lastAdvancePath)
    }
  }

  showPaymentModal() {
    this.paymentModalTarget.classList.remove("hidden")
  }

  hidePaymentModal() {
    this.paymentModalTarget.classList.add("hidden")
  }

  showTaxNoExistNotification() {
    showNotification(this.translationsValue["moss_tax_no_exist"])
  }

  showTaxNoNotExistNotification() {
    showNotification(this.translationsValue["des_tax_no_not_exist"])
  }

  tickShouldUpdateDepartment() {
    this.element.querySelector("#invoice_should_update_department").checked = true
  }

  // deleguje do invoice--tax-rate (właściciela logiki stawek VAT) — patrz tax_rate_controller.js
  zeroTaxAlert() {
    this.invoiceTaxRateOutlet.zeroTaxAlert()
  }

  // invoice--reverse-charge jest renderowany tylko przy is_pl_reverse_charge, stąd tablicowy
  // outlet zamiast pojedynczego — brak instancji nie jest błędem, tylko forEach nic nie robi
  notifyReverseChargeOfCountryChange() {
    this.invoiceReverseChargeOutlets.forEach((rc) => rc.updateBuyerCountry())
  }

  notifyReverseChargeOfNpTaxKindInner(event) {
    this.invoiceReverseChargeOutlets.forEach((rc) => rc.checkNpTaxKindInner(event))
  }

  confirmReverseChargeTypeChange(event) {
    const { changedLocation, confirmMsg } = event.params
    if (confirm(confirmMsg)) {
      Turbo.visit(changedLocation)
    } else {
      event.target.checked = !event.target.checked
    }
  }

  updateViesVisibility() {
    if (this.hasViesButtonTarget) {
      if (this.euCountriesValue.includes(this.buyerCountryTarget.value) && this.euCountriesValue.includes(this.sellerCountryTarget.value) &&
        ["vat", "proforma", "wdt", "export_products"].includes(this.kindValue) && this.buyerCountryTarget.value !== this.sellerCountryTarget.value) {
        this.viesButtonTarget.classList.remove("hidden")
      } else {
        this.viesButtonTarget.classList.add("hidden")
      }
    }
  }

  updateDeliveryAddressVisibility(event) { // #TODO usunac po przebudowie dok mag
    if (event.currentTarget.checked) {
      this.deliveryAddressWrapperTarget.classList.remove("hidden")
    } else {
      this.deliveryAddressWrapperTarget.classList.add("hidden")
    }
  }

  updatePostalAddress(event) {
    const isChecked = event.target.checked
    const prefix = event.params.kind
    const destroyCheckbox = this.targets.find(`${prefix}PostalAddressDestroy`)
    if (!isChecked) {
      destroyCheckbox.checked = true
    } else {
      destroyCheckbox.checked = false
    }
  }

  clearPostalAddressForm(event) {
    const prefix = event.params.prefix
    if (!prefix) return

    const wrapper = this.targets.find(`${prefix}Wrapper`)
    wrapper.querySelectorAll("input[type=\"text\"], textarea").forEach(el => {
      el.value = ""
    })
    wrapper.querySelectorAll("select").forEach(el => {
      el.selectedIndex = 0
    })
  }

  updatePriceList(event) {
    if (this.defaultPriceList === event.target.value || !confirm(this.translationsValue["price_list_change_confirm"])) {
      event.target.value = this.defaultPriceList
      return
    }

    this.defaultPriceList = event.target.value

    this.recalculatePositionsForNewPriceList()

    this.currencyTarget.value = event.target.selectedOptions[0].getAttribute("data-currency") || this.defaultCurrencyValue
    this.currencyTarget.dispatchEvent(new Event("change"))
  }

  recalculatePositionsForNewPriceList() {
    const params = new URLSearchParams()
    this.invoicePositionCalculatorOutlets.forEach(position => {
      if (position.hasProductIdTarget && position.productIdTarget.value.length > 0) {
        params.append("product_ids[]", position.productIdTarget.value)
      }
    })

    if (params.size === 0) {
      return
    }

    const priceList = this.defaultPriceList || 0

    const attrs = ["tax", "price_net", "price_gross", "currency"]
    Rails.ajax({
      url: "/price_lists/" + priceList + "/prices_for_products.json",
      type: "get",
      data: params,
      success: (data) => {
        for (const position of this.invoicePositionCalculatorOutlets) {
          if (!position.hasProductIdTarget || position.productIdTarget.value.length === 0) continue

          attrs.forEach((attr) => {
            if (attr === "tax") {
              forceSetValueOnSelect(document.getElementById(`invoice_positions_attributes_${position.positionIdValue}_tax`), data[position.productIdTarget.value][attr])
            } else {
              document.getElementById(`invoice_positions_attributes_${position.positionIdValue}_${attr}`).value = data[position.productIdTarget.value][attr]
            }
          })
          document.getElementById(`invoice_positions_attributes_${position.positionIdValue}_tax`).dispatchEvent(new Event("change"))
        }
      }
    })
  }

  checkRoundPrice(event) {
    if (event.params.roundPriceDefault === "cash") {
      this.roundPriceTarget.checked = (event.target.value === "cash")
    } else if (event.params.roundPriceDefault === "always") {
      this.roundPriceTarget.checked = true
    }
  }

  setBankAccountNumberFromInvoice() {
    if (this.fromInvoiceSellerBankAccountValue &&
      this.departmentsValue[this.departmentSelectTarget.value]["bank_account"] &&
      !this.sellerBankAccountConnectedWithDepartmentValue &&
      document.getElementById("allowed_to_change_bank_account").value === "true") {
      if (confirm(this.translationsValue["bank_account_has_changed"] + " " + this.translationsValue["change_bank_account_to_source_invoice"])) {
        this.sellerBankAccountTarget.value = this.fromInvoiceSellerBankAccountValue
        this.sellerBankTarget.value = this.fromInvoiceSellerBankValue
        this.sellerBankSwiftTarget.value = this.fromInvoiceSellerBankSwiftValue
        document.getElementById("keep_bank_account_number_from_invoice").value = true

        if (this.hasSellerBankAccountInfoTarget) {
          this.sellerBankAccountInfoTarget.innerText = this.sellerBankTarget.value + " " + this.sellerBankAccountTarget.value
        }
      }
    } else if (this.fromInvoiceSellerBankAccountValue &&
      this.departmentsValue[this.departmentSelectTarget.value]["bank_account"] &&
      !this.sellerBankAccountConnectedWithDepartmentValue &&
      document.getElementById("allowed_to_change_bank_account").value === "false") {
      showNotification(this.translationsValue["bank_account_has_changed"] + " " + this.translationsValue["changed_bank_account_to_default"])
    }
  }

  showGtuCodesOnInvoiceOnlyAlert() {
    if (this.newRecordValue && this.showGtuAlertsValue) {
      alert(this.translationsValue["gtu_codes_on_invoice_only"])
    }
  }

  showAttachments() {
    if (this.hasShowAttachmentsTarget) {
      this.showAttachmentsTarget.classList.remove("hidden")
    }
  }

  numberValidation(event) {
    const withMinus = event.params.withMinus
    const hasMinus = event.target.value.startsWith("-")
    event.target.value = event.target.value.replace(/[^\d]/g, '')
    if (hasMinus && withMinus) {
      event.target.value = "-" + event.target.value
    }
  }

  showBuyerEmailChangeWarn() {
    if (!isEmpty(document.getElementById("invoice_client_id").value)) {
      if (!isEmpty(this.buyerEmailTarget.value) && this.buyerEmailTarget.value !== document.getElementById("buyer_email_check").value) {
        showNotification(this.translationsValue['buyer_email_change_only_client_warn'])
      }
    }
  }

  deleteAttachment(event) {
    if (confirm(this.translationsValue["delete_confirm"])) {
      document.getElementById("invoice_attachment_delete_" + event.params.id).checked = true
      document.getElementById("file-" + event.params.id).classList.add("h-0")
      document.getElementById("file-" + event.params.id).classList.add("hidden")
    }
  }

  editionBlockedNotification() {
    showNotification(this.translationsValue["gov_lock"])
  }

  setSendToGov() {
    if (!this.hasGovSendTarget) return false

    this.govSendTarget.value = true
  }

  // Dynamicznie aktualizuje przycisk "Zapisz i wyślij do KSeF" na podstawie NIPu sprzedawcy
  updateGovSendButton() {
    if (!this.hasGovSendBtnTarget) return

    const taxNoField = this.element.querySelector('#invoice_seller_tax_no')
    const taxNo = taxNoField?.value
    if (!taxNo) {
      this._setGovButtonState('not_allowed')
      return
    }

    // Normalizacja: usunięcie spacji/myślników, uppercase, dodanie prefiksu kraju
    let normalized = taxNo.replace(/[\s-]/g, '').toUpperCase()
    if (!/^[A-Z]/.test(normalized)) {
      normalized = (this.accountCountryValue || '').toUpperCase() + normalized
    }

    const permission = this.govPermissionsValue[normalized]
    this._setGovButtonState(permission || 'not_allowed')
  }

  _setGovButtonState(permission) {
    const btn = this.govSendBtnTarget
    const saveBtn = this.hasGovSendSaveBtnTarget ? this.govSendSaveBtnTarget : null
    const primarySaveBtn = this.hasGovSavePrimaryBtnTarget ? this.govSavePrimaryBtnTarget : null

    if (permission === 'can_send') {
      btn.classList.remove('hidden')
      btn.disabled = false
      btn.classList.remove('opacity-50', 'cursor-not-allowed')
      btn.title = ''
      if (saveBtn) saveBtn.classList.remove('hidden')
      if (primarySaveBtn) primarySaveBtn.classList.add('hidden')
    } else if (permission === 'needs_authorization') {
      btn.classList.remove('hidden')
      btn.disabled = true
      btn.classList.add('opacity-50', 'cursor-not-allowed')
      btn.title = this.translationsValue['invoice.gov_authorize_first'] || 'Najpierw zautoryzuj się w KSeF'
      if (saveBtn) saveBtn.classList.remove('hidden')
      if (primarySaveBtn) primarySaveBtn.classList.add('hidden')
    } else {
      // not_allowed lub brak - ukrywamy przyciski gov, pokazujemy zwykły "Zapisz"
      btn.classList.add('hidden')
      if (saveBtn) saveBtn.classList.add('hidden')
      if (primarySaveBtn) primarySaveBtn.classList.remove('hidden')
    }
  }

  updateTax(event) {
    const details = event.detail

    if (details.role !== "seller") return
    this.updateGovSendButton()
  }

  checkBankAccountCurrency() {
    if (!this.hasDropdownInvoiceBankAccountOutlet || !this.hasCurrencyTarget) {
      return true
    }

    const invoiceCurrency = this.currencyTarget.value
    if (!invoiceCurrency) {
      return true
    }

    const selectedAccounts = this.dropdownInvoiceBankAccountOutlet.selectedAccounts
    if (!selectedAccounts || selectedAccounts.length === 0) {
      return true
    }

    const accountsWithCurrency = selectedAccounts.filter(account => account.bank_currency)
    if (accountsWithCurrency.length === 0) {
      return true
    }

    const hasMatchingCurrency = accountsWithCurrency.some(account =>
      account.bank_currency === invoiceCurrency
    )

    if (!hasMatchingCurrency) {
      return confirm(this.translationsValue.currency_not_matching_bank_accounts)
    }

    return true
  }
}
